import { z } from 'zod';

/**
 * Desired-state configuration model (see DEPLOYMENT_ENGINE.md). A revision is an
 * immutable set of resource specs; the planner renders these to per-edge bundles
 * that the edge's resource handlers inspect/plan/apply/verify.
 */

/** Managed resource types. Each maps to an edge resource handler. */
export const ResourceType = {
  PACKAGE: 'package',
  SYSTEMD_SERVICE: 'systemd-service',
  SYSCTL: 'sysctl',
  WIREGUARD: 'wireguard',
  FRR: 'frr',
  NFTABLES: 'nftables',
  IPROUTE: 'iproute',
  ROUTE: 'route',
  POLICY_RULE: 'policy-rule',
  CERTIFICATE: 'certificate',
  HEALTH_CHECK: 'health-check',
  AGENT_CONFIG: 'agent-config',
} as const;
export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

export const RESOURCE_TYPES = Object.values(ResourceType);

/** Where a resource applies. */
export const targetScopeSchema = z.object({
  scope: z.enum(['tenant', 'fabric', 'site', 'device']),
  id: z.string().uuid().optional(),
});
export type TargetScope = z.infer<typeof targetScopeSchema>;

/** A single declarative resource. `key` is stable within its type + target. */
export const resourceSpecSchema = z.object({
  type: z.enum(RESOURCE_TYPES as [ResourceType, ...ResourceType[]]),
  key: z.string().min(1),
  target: targetScopeSchema,
  /** Declarative parameters for the resource; validated per handler. */
  spec: z.record(z.unknown()).default({}),
  /** When true, the resource is removed rather than applied. */
  absent: z.boolean().optional(),
});
export type ResourceSpec = z.infer<typeof resourceSpecSchema>;

/** The full desired state authored into a revision. */
export const desiredStateSchema = z.object({
  resources: z.array(resourceSpecSchema).default([]),
});
export type DesiredState = z.infer<typeof desiredStateSchema>;

/** Planned action for a resource on a specific edge (diff vs last-applied). */
export type ResourceAction = 'create' | 'update' | 'remove' | 'noop';

export interface PlannedResource {
  type: ResourceType;
  key: string;
  action: ResourceAction;
  spec: Record<string, unknown>;
}

/** A per-edge rendered bundle: the ordered resource actions to apply. */
export interface EdgeBundleContent {
  deviceId: string;
  tenantId: string;
  revisionNumber: number;
  resources: PlannedResource[];
}
